import type { ComponentProps, KeyboardEvent, ReactNode } from 'react';
import type { SortType, TableProps } from 'rsuite';
import type { DropdownOption } from '../Dropdown/DropdownOption/DropdownOption';
import type { CheckCellProps, DataGridCheckRow } from './CheckCell/CheckCell';
import type { DataGridGetRowLinkOptionsFn } from './RowCell/RowCell';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Table } from 'rsuite';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import CheckCell from './CheckCell/CheckCell';
import ColumnSettingsPopover from './ColumnSettingsPopover/ColumnSettingsPopover';
import RowCell from './RowCell/RowCell';

export type { DataGridGetRowLinkOptionsFn } from './RowCell/RowCell';

const { Column, HeaderCell, Cell } = Table;

export type DataGridSortColumnFn = (column?: string, type?: SortType) => void;

export type DataGridCheckRowsFn = (checked: (string | number)[]) => void;

export type DataGridColumnResizeFn = (
  columnWidth?: number,
  dataKey?: string,
) => void;

export interface DataGridColumnProps {
  accessor: string;
  label: ReactNode;
  render: (value: any, rowData: any) => ReactNode;
  sortable?: boolean;
  additionalClasses?: string;
  flexGrow?: number;
  width?: number;
  minWidth?: number;
  justify?: string;
  hide?: boolean;
  resizable?: boolean;
  fixed?: boolean | 'left' | 'right';
  align?: 'left' | 'center' | 'right';
  removable?: boolean;
  showByDefault?: boolean;
  baseWidth?: number;
  filterInputType?: string;
  filterInputOptions?: DropdownOption[] | string[];
  filterDatasourceFetch?: (
    query?: string,
  ) => Promise<DropdownOption[]> | undefined;
}

export interface DataGridProps {
  /**
   * DataGrid data
   */
  data: any[];
  /**
   * Additional classes for button
   */
  additionalClasses?: string;
  /**
   * Array of column definitions
   */
  columns: DataGridColumnProps[];
  /**
   * Show/Hide select column
   */
  hasSelectColumn?: boolean;
  /**
   * Sorting callback
   */
  onSortColumn?: DataGridSortColumnFn;
  /**
   * Waiting for data state
   */
  isLoading?: boolean;
  /**
   * Initial column for sorting
   */
  sort?: string;
  /**
   * Initial sort type
   */
  sortOrder?: SortType;
  /**
   * Array of checked ids
   */
  checkedRows?: DataGridCheckRow[];
  /**
   * Function firing on checking row checkboxes (keys: array) => void
   */
  onCheckRows?: DataGridCheckRowsFn;
  /**
   * DataGrid Height
   */
  tableHeight?: number;
  /**
   * The height of the DataGrid will be automatically expanded according
   * to the number of data rows, and no vertical scroll bar will appear.
   */
  autoHeight?: boolean;
  /**
   * If table should be expanded to full height of the parent container
   */
  fillHeight?: boolean;
  /**
   * DataGrid row height
   */
  rowHeight?: number;
  /**
   * Header Height
   */
  headerHeight?: number;
  /**
   * DataGrid status bar under table
   */
  statusBar?: ReactNode;
  /**
   * DataGrid no data info text
   */
  noDataInfo?: ReactNode;
  /**
   * DataGrid loading data info text
   */
  loadingDataInfoText?: string;
  /**
   * Loading data icon
   */
  loadingIcon?: ReactNode;
  /**
   * Data grid testid
   */
  testId?: string;
  /**
   * Data grid resize handler
   */
  onResize?: DataGridColumnResizeFn;
  /**
   * Data grid show/hide sort options in header column.
   */
  sortableColumns?: boolean;
  /**
   * Callback to generate link options for every cell
   */
  getRowLinkOptions?: DataGridGetRowLinkOptionsFn;
  /**
   * Callback for updates made through the column settings control.
   * When provided, DataGrid renders the column settings action column.
   */
  onColumnSettingsChange?: (columns: DataGridColumnProps[]) => void;
}

const DataGrid = ({
  columns,
  hasSelectColumn = false,
  isLoading = false,
  data = EMPTY_ARRAY,
  additionalClasses,
  onSortColumn,
  sort = '',
  sortOrder = 'asc',
  checkedRows = EMPTY_ARRAY,
  onCheckRows,
  tableHeight = 300,
  autoHeight = true,
  fillHeight = false,
  rowHeight,
  headerHeight = 60,
  statusBar,
  noDataInfo,
  loadingDataInfoText = '',
  loadingIcon,
  testId,
  onResize,
  sortableColumns = true,
  getRowLinkOptions,
  onColumnSettingsChange,
}: DataGridProps) => {
  const [gridLoading, setGridLoading] = useState(isLoading);

  useEffect(() => {
    if (!isLoading) setTimeout(() => setGridLoading(isLoading), 50);
    else setGridLoading(isLoading);
  }, [isLoading]);

  const [checkedKeys, setCheckedKeys] = useState(checkedRows || []);

  useEffect(() => {
    if (Array.isArray(checkedRows)) setCheckedKeys(checkedRows);
  }, [checkedRows]);

  /**
   * Find common keys between data ids and checked keys
   */
  const dataCheckedKeys = useMemo(() => {
    const checkedDictionary = checkedKeys.reduce(
      (acc, id) => {
        acc[id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    return data.map((row) => row.id).filter((id) => checkedDictionary[id]);
  }, [data, checkedKeys]);

  const checked = useMemo(
    () => dataCheckedKeys.length === data.length,
    [data, dataCheckedKeys],
  );

  const indeterminate = useMemo(
    () => dataCheckedKeys.length > 0 && dataCheckedKeys.length < data.length,
    [data, dataCheckedKeys],
  );

  const handleMultiSelect = useCallback(
    (value: string | number) => {
      const lasKey = checkedKeys[checkedKeys.length - 1];
      let keys: (string | number)[];

      if (!lasKey) {
        keys = [...checkedKeys, value];
      } else {
        const indexes: number[] = [];

        data.forEach((element, key) => {
          if ([lasKey, value].includes(element.id)) indexes.push(key);
        });

        keys = [
          ...checkedKeys,
          ...data
            .slice(indexes[0], indexes[1] + 1)
            .map((element) => element.id),
        ];
      }
      keys = [...new Set(keys)];
      setCheckedKeys(keys);
      onCheckRows?.(keys);
    },
    [checkedKeys, data, onCheckRows],
  );

  const handleCheckAll = useCallback<
    Exclude<ComponentProps<typeof Checkbox>['onChange'], undefined>
  >(
    (_, isChecked) => {
      const keys = isChecked
        ? [...new Set([...checkedKeys, ...data.map((item) => item.id)])]
        : checkedKeys.filter(
            (checked) => data.findIndex((item) => item.id === checked) < 0,
          );
      setCheckedKeys(keys);
      onCheckRows?.(keys);
    },
    [data, onCheckRows, checkedKeys],
  );

  const handleCheck = useCallback<
    Exclude<CheckCellProps['onChange'], undefined>
  >(
    (value, isChecked, event) => {
      if (
        isChecked &&
        (event.nativeEvent as unknown as KeyboardEvent<HTMLInputElement>)
          .shiftKey
      ) {
        handleMultiSelect(value as string);
        return;
      }

      let keys: (string | number)[];
      if (isChecked) {
        keys = value !== undefined ? [...checkedKeys, value] : checkedKeys;
      } else {
        keys = checkedKeys.filter((item) => item !== value);
      }
      setCheckedKeys(keys);
      onCheckRows?.(keys);
    },
    [checkedKeys, handleMultiSelect, onCheckRows],
  );

  const [currentSort, setCurrentSort] = useState<string | undefined>(sort);
  const [currentOrder, setCurrentOrder] = useState<SortType | undefined>(
    sortOrder,
  );

  const handleSortColumn = useCallback<
    Exclude<TableProps['onSortColumn'], undefined>
  >(
    (column, type) => {
      let newSortType = type;
      let newSortColumn: string | undefined = column;

      if (currentSort === column) {
        if (currentOrder === 'asc') {
          newSortType = 'desc';
        } else if (currentOrder === 'desc') {
          newSortType = newSortColumn = undefined;
        }
      } else {
        newSortType = 'asc';
      }

      setCurrentSort(newSortColumn);
      setCurrentOrder(newSortType);
      onSortColumn?.(newSortColumn, newSortType);
    },
    [onSortColumn, currentSort, currentOrder],
  );

  useEffect(() => {
    setCurrentSort(sort);
    setCurrentOrder(sortOrder);
  }, [sort, sortOrder]);

  return (
    <>
      <Table
        className={twMerge(
          'w-full font-semibold bg-white dark:bg-slate-950',
          'text-slate-900 dark:text-slate-200 border border-slate-100 dark:border-slate-800 rounded-t-lg',
          statusBar ? 'rounded-b-none' : 'rounded-b-lg',
          additionalClasses,
        )}
        height={tableHeight}
        fillHeight={fillHeight}
        autoHeight={autoHeight}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        wordWrap={false}
        data={data}
        sortColumn={currentSort}
        sortType={currentOrder}
        onSortColumn={handleSortColumn}
        loading={gridLoading}
        renderEmpty={() => noDataInfo}
        renderLoading={() => (
          <div
            className={twMerge(
              'absolute flex items-center flex-col',
              'justify-center w-full h-full bg-white/90 dark:bg-slate-900/90 z-10',
            )}
            role="status"
          >
            {loadingIcon}
            <span className="mt-2">{loadingDataInfoText}</span>
          </div>
        )}
        {...getTestProps(testId, 'table')}
      >
        {hasSelectColumn && (
          <Column width={50} flexGrow={0} fixed="left">
            <HeaderCell className="cell-column">
              <div className="flex h-full items-center justify-center px-4 py-2">
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                  disabled={isLoading || !data.length}
                  id="header-checkbox"
                  {...getTestProps(testId, 'header-checkbox')}
                />
              </div>
            </HeaderCell>
            <CheckCell
              dataKey="id"
              checkedKeys={checkedKeys}
              onChange={handleCheck}
              className="cell-column"
            />
          </Column>
        )}

        {columns.map(
          (column, idx) =>
            !column.hide && (
              <Column
                key={column.accessor}
                fullText={true}
                width={column.width}
                minWidth={column.minWidth}
                sortable={sortableColumns ? column.sortable : false}
                {...(!column.resizable && {
                  flexGrow: column.flexGrow ?? 1,
                })}
                resizable={column.resizable}
                onResize={onResize}
                fixed={column.fixed}
                align={column.align}
                {...getTestProps(testId, `${idx}-column`)}
              >
                <HeaderCell>
                  <div
                    className="flex h-full w-full items-center px-4 py-2"
                    {...getTestProps(
                      testId,
                      `header-column-${column.accessor}`,
                    )}
                  >
                    <div className="truncate">{column.label}</div>
                  </div>
                </HeaderCell>
                <RowCell
                  className="text-sm lg:text-base font-normal"
                  dataKey={column.accessor}
                  accessor={column.accessor}
                  renderCell={column.render}
                  getRowLinkOptions={getRowLinkOptions}
                  testId={testId}
                />
              </Column>
            ),
        )}
        {onColumnSettingsChange && (
          <Column
            key="column-settings-actions"
            width={56}
            minWidth={56}
            flexGrow={0}
            fixed="right"
            resizable={false}
            sortable={false}
          >
            <HeaderCell className="cell-column">
              <div className="h-7 w-7" aria-hidden="true" />
            </HeaderCell>
            <Cell dataKey="id">
              <span aria-hidden="true" />
            </Cell>
          </Column>
        )}
      </Table>

      {/* Button has to be outside the table. When columns are added/removed,
          table is re-rendered and the button would close the popover */}
      {onColumnSettingsChange && (
        <div
          className="absolute right-3.5 z-20"
          style={{ top: (headerHeight - 28) / 2 }}
        >
          <ColumnSettingsPopover
            columns={columns}
            onChange={onColumnSettingsChange}
            testId={testId}
          />
        </div>
      )}
      {statusBar && (
        <div {...getTestProps(testId, 'status-bar')}>{statusBar}</div>
      )}
    </>
  );
};

export default DataGrid;
